import { memo, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';

import { Badge, Button, DataTableSelectionCheckbox } from 'design-system';
import { LockIcon } from 'lucide-react';

import type { ColumnVisibilityState } from '@tanstack/react-table';

import type { CatalogItem } from '@/entities/catalog-item';
import { Permission } from '@/entities/permissions';
import type { SymbolKey } from '@/entities/symbol';
import { isSameSymbol, isSameSymbolIgnoringPeriod } from '@/entities/symbol';
import { DateTextVariants, useDateText, usePermission } from '@/features/_shared/user';
import { requestSpotSelection, SELECTOR_MODES } from '@/features/symbol-selector';
import { formatNumberWithPrecision } from '@/shared/lib/format/formatNumberWithPrecision';
import { getDiffColor } from '@/shared/lib/format/getDiffColor';
import { shallowEqual, useAppDispatch, useAppSelector } from '@/store/hooks';

import ViewInOtherCatalogLink from '../ViewInOtherCatalogLink/ViewInOtherCatalogLink';
import { PARTIAL_PERIOD_COUNT, PERIOD_COLUMNS } from './periodColumns';

type PeriodDetailPanelProps = Readonly<{
  periods: CatalogItem[];
  showAll: boolean;
  onShowAll: () => void;
  isInDashboard: boolean;
  renderFactsheetButton?: (code: string, mode: 'modal' | 'newTab') => ReactNode;
  columnVisibility: ColumnVisibilityState;
  onViewInForwardCurves?: () => void;
}>;

function PeriodSymbolSelection({ disabled, symbol }: { disabled: boolean; symbol?: SymbolKey }) {
  const dispatch = useAppDispatch();
  const { checked, locked } = useAppSelector(state => {
    const isLocked = state.symbolSelector.selectorMode === SELECTOR_MODES.FORWARD_CURVE;
    const equals = isLocked ? isSameSymbolIgnoringPeriod : isSameSymbol;
    return {
      checked: symbol
        ? state.symbolSelector.symbols.some(selected => equals(selected, symbol))
        : false,
      locked: isLocked
    };
  }, shallowEqual);

  const onToggle = useCallback(() => {
    if (symbol) dispatch(requestSpotSelection({ symbol }));
  }, [dispatch, symbol]);

  return (
    <DataTableSelectionCheckbox
      aria-label={symbol ? `Select ${symbol.Code} ${symbol.Period}` : 'Select period'}
      snapshot={{
        checked,
        indeterminate: false,
        disabled: disabled || !symbol,
        locked
      }}
      onToggle={onToggle}
    />
  );
}

const PeriodDetailPanel = ({
  periods,
  showAll,
  onShowAll,
  isInDashboard,
  renderFactsheetButton,
  columnVisibility,
  onViewInForwardCurves
}: PeriodDetailPanelProps) => {
  const { hasPermission } = usePermission();
  const isPermissionGranted = hasPermission(Permission.ViewCatalogDetails);
  const { formatDateString } = useDateText();

  const visibleColumns = useMemo(
    () => PERIOD_COLUMNS.filter(col => col.alwaysVisible || (columnVisibility[col.key] ?? true)),
    [columnVisibility]
  );

  const visiblePeriods = showAll ? periods : periods.slice(0, PARTIAL_PERIOD_COUNT);
  const remaining = Math.max(0, periods.length - visiblePeriods.length);

  const renderValue = (
    period: CatalogItem,
    key: string,
    kind: string | undefined,
    restricted: boolean
  ) => {
    if ((key === 'Last' || key === 'Change') && restricted) {
      return (
        <LockIcon
          aria-label='Restricted value'
          className='size-4 text-muted-foreground'
        />
      );
    }

    const value = period[key];
    let raw = '';
    if (typeof value === 'string') raw = value;
    else if (typeof value === 'number') raw = value.toString();
    if (kind === 'number') return formatNumberWithPrecision(raw);
    if (kind === 'date') return formatDateString(raw, DateTextVariants.FULL);
    return raw;
  };

  return (
    <div
      className='bg-gx-tile-dark pl-2'
      data-testid='period-detail-panel'
    >
      <div className='flex h-7 items-center'>
        <div className='w-[35px] shrink-0' />
        <div className='w-[50px] shrink-0' />
        {visibleColumns.map(column => (
          <div
            key={column.key}
            className='flex shrink-0 px-2 text-sm font-bold'
            style={{
              width: column.width,
              justifyContent: column.align ?? 'flex-start'
            }}
          >
            {column.label}
          </div>
        ))}
      </div>

      {visiblePeriods.map(period => {
        const restricted = !isPermissionGranted || (period.Last === '' && period.Change === '');
        return (
          <div
            key={`${period.Code}-${period.Period}-${period.PeriodType}-${period.TimeRef}`}
            className='flex min-h-6 items-center'
          >
            <div className='flex w-[35px] shrink-0 items-center pl-2'>
              <PeriodSymbolSelection
                disabled={restricted}
                symbol={period.Symbol}
              />
            </div>
            <div className='flex w-[50px] shrink-0 justify-center'>
              {renderFactsheetButton?.(period.Code, isInDashboard ? 'newTab' : 'modal')}
            </div>
            {visibleColumns.map(column => (
              <div
                key={column.key}
                className='flex shrink-0 px-2 text-sm text-muted-foreground'
                style={{
                  width: column.width,
                  justifyContent: column.align ?? 'flex-start',
                  color:
                    column.key === 'Change' && !restricted
                      ? getDiffColor(Number(period.Change))
                      : undefined
                }}
              >
                {renderValue(period, column.key, column.kind, restricted)}
              </div>
            ))}
          </div>
        );
      })}

      <div className='flex min-h-9 items-center gap-3 pl-[85px]'>
        {!showAll && remaining > 0 ? (
          <Button
            aria-label={`+${remaining} more periods`}
            data-row-action
            size='sm'
            variant='link'
            onClick={onShowAll}
          >
            <Badge variant='muted'>+{remaining}</Badge>
            more periods
          </Button>
        ) : null}
        {onViewInForwardCurves ? (
          <ViewInOtherCatalogLink
            label='View in Forward Curves'
            onClick={onViewInForwardCurves}
          />
        ) : null}
      </div>
    </div>
  );
};

export default memo(PeriodDetailPanel);
